"use client";

import { c as _c } from "react/compiler-runtime";
import { X, Flag } from "lucide-react";
import { useState } from "react";
import { Button } from "@/src/components/shadcn/ui/button";
import { Card } from "@/src/components/shadcn/ui/card";
import { Label } from "@/src/components/shadcn/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/src/components/shadcn/ui/tooltip";
import { EntityMatcher } from "@/src/components/tasks/intake-sections/matchers";
import type { DiagnosisItem } from "@/src/models/documents/referral";
import type { ICD10 } from "@/src/models/icd10";
import { formatICD10CodeForDisplay, normalizeICD10Code } from "@/src/models/icd10";
interface ICD10DiagnosisFormProps {
  items: DiagnosisItem[];
  onChange: (items: DiagnosisItem[]) => void;
  label?: string;
  isRequired?: boolean;
  fieldKey?: string;
  doesConflict?: boolean;
  onFlagForInaccuracyUpdate?: (key: string, value: any) => void;
  metadata?: any;
  setMetadata?: (metadata: any) => void;
  clearMetadata?: () => void;
}
export default function ICD10DiagnosisForm(t0) {
  const $ = _c(49);
  const {
    items: t1,
    onChange,
    label: t2,
    isRequired: t3,
    fieldKey,
    doesConflict,
    onFlagForInaccuracyUpdate,
    metadata,
    setMetadata,
    clearMetadata
  } = t0;
  let t4;
  if ($[0] !== t1) {
    t4 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const items = t4;
  const label = t2 === undefined ? "Diagnosis List" : t2;
  t3 === undefined ? false : t3;
  const [showActionButtons, setShowActionButtons] = useState(false);
  let t5;
  if ($[2] !== onChange) {
    t5 = newItems => {
      setShowActionButtons(true);
      onChange(newItems);
    };
    $[2] = onChange;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const triggerChange = t5;
  let t6;
  if ($[4] !== fieldKey || $[5] !== items || $[6] !== onFlagForInaccuracyUpdate) {
    t6 = () => {
      setShowActionButtons(false);
      onFlagForInaccuracyUpdate?.(fieldKey || "", items);
    };
    $[4] = fieldKey;
    $[5] = items;
    $[6] = onFlagForInaccuracyUpdate;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleSave = t6;
  let t7;
  if ($[8] !== items || $[9] !== triggerChange) {
    t7 = icd10 => {
      if (!icd10) {
        return;
      }
      const newItem = {
        diagnosisCode: icd10.code,
        diagnosisText: icd10.description,
        diagnosisCodeType: "ICD-10"
      };
      const normalizedNewCode = normalizeICD10Code(newItem.diagnosisCode);
      const isDuplicate = items.some(item => normalizeICD10Code(item.diagnosisCode) === normalizedNewCode);
      if (!isDuplicate) {
        triggerChange([...items, newItem]);
      }
    };
    $[8] = items;
    $[9] = triggerChange;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleICD10Select = t7;
  let t8;
  if ($[11] !== items || $[12] !== triggerChange) {
    t8 = index => {
      const itemToRemove = items[index];
      const updatedItems = items.filter(item_0 => normalizeICD10Code(item_0.diagnosisCode) !== normalizeICD10Code(itemToRemove.diagnosisCode) || item_0.diagnosisText !== itemToRemove.diagnosisText);
      triggerChange(updatedItems);
    };
    $[11] = items;
    $[12] = triggerChange;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const handleRemoveItem = t8;
  const getDisplayValue = _temp;
  let t9;
  if ($[14] !== handleSave || $[15] !== label || $[16] !== onFlagForInaccuracyUpdate || $[17] !== showActionButtons) {
    t9 = label && <div className="flex items-center justify-between"><Label className="text-muted-foreground">{label}</Label>{onFlagForInaccuracyUpdate && showActionButtons && <TooltipProvider><Tooltip delayDuration={2000}><TooltipTrigger asChild={true}><Flag className="h-3 w-3 text-orange-500 hover:text-orange-700" onClick={e => {
              e.stopPropagation();
              handleSave();
            }} /></TooltipTrigger><TooltipContent><span>Flag for AI extraction inaccuracy</span></TooltipContent></Tooltip></TooltipProvider>}</div>;
    $[14] = handleSave;
    $[15] = label;
    $[16] = onFlagForInaccuracyUpdate;
    $[17] = showActionButtons;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = `mt-2 p-4 w-full border-muted/80 rounded-sm shadow-sm ${doesConflict ? "bg-muted/10" : "bg-muted/5"}`;
  const t11 = doesConflict ? "orange" : "";
  let t12;
  if ($[19] !== t11) {
    t12 = {
      borderColor: t11
    };
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = [];
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== handleICD10Select) {
    t14 = <div className="flex justify-center items-center mb-4"><div className="w-full"><EntityMatcher type="icd10" existingEntities={t13} isLoadingExistingEntities={false} entity={null} onEntitySelected={handleICD10Select} showCreateNewOption={false} /></div></div>;
    $[22] = handleICD10Select;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const t15 = `space-y-2 ${doesConflict ? "text-muted-foreground" : ""}`;
  let t16;
  if ($[24] !== clearMetadata || $[25] !== handleRemoveItem || $[26] !== items || $[27] !== metadata || $[28] !== setMetadata) {
    let t17;
    if ($[30] !== clearMetadata || $[31] !== handleRemoveItem || $[32] !== metadata || $[33] !== setMetadata) {
      t17 = (item_2, index_0) => <li key={index_0} className="space-y-2"><div className="flex space-x-2 items-center"><span className="flex-grow text-sm overflow-hidden text-ellipsis" tabIndex={0} onFocus={() => {
            if (setMetadata && metadata?.diagnosisList?.[index_0] !== undefined) {
              const itemMetadata = metadata.diagnosisList[index_0];
              const metadataToUse = itemMetadata.diagnosisCode_metadata || itemMetadata.diagnosisText_metadata;
              if (metadataToUse) {
                setMetadata(metadataToUse);
              }
            }
          }} onBlur={() => {
            if (clearMetadata) {
              clearMetadata();
            }
          }}>{getDisplayValue(item_2)}</span><div className="flex shrink-0 ml-auto"><Button size="icon" variant="ghost" onClick={() => handleRemoveItem(index_0)}><X className="w-4 h-4" /></Button></div></div></li>;
      $[30] = clearMetadata;
      $[31] = handleRemoveItem;
      $[32] = metadata;
      $[33] = setMetadata;
      $[34] = t17;
    } else {
      t17 = $[34];
    }
    t16 = items.map(t17);
    $[24] = clearMetadata;
    $[25] = handleRemoveItem;
    $[26] = items;
    $[27] = metadata;
    $[28] = setMetadata;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[35] !== t15 || $[36] !== t16) {
    t17 = <ul className={t15}>{t16}</ul>;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== doesConflict) {
    t18 = doesConflict && <div className="flex items-center mt-3 text-xs text-muted-foreground justify-between"><span className="ml-1 pl-1">{doesConflict ? "Using matched data" : "Showing document data (matched data available)"}</span></div>;
    $[38] = doesConflict;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t10 || $[41] !== t12 || $[42] !== t14 || $[43] !== t17 || $[44] !== t18) {
    t19 = <Card className={t10} style={t12}>{t14}{t17}{t18}</Card>;
    $[40] = t10;
    $[41] = t12;
    $[42] = t14;
    $[43] = t17;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t19 || $[47] !== t9) {
    t20 = <div className="px-1">{t9}{t19}</div>;
    $[46] = t19;
    $[47] = t9;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  return t20;
}
function _temp(item_1) {
  const formattedCode = formatICD10CodeForDisplay(item_1.diagnosisCode);
  return `${formattedCode} ${item_1.diagnosisText || ""}`.trim();
}